/**
 * BillingTab — iter81: Admin "Billing Control" section.
 * Readiness checklist, master payments toggle (guarded + confirmed),
 * live PRO status, side-effect-free Stripe self-test, dashboard deep links.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TabsContent } from "../ui/tabs";
import { Switch } from "../ui/switch";
import {
  CreditCard, CheckCircle2, XCircle, RefreshCw, ExternalLink,
  ShieldAlert, FlaskConical, Users, TrendingUp, Activity,
} from "lucide-react";
import { BACKEND_URL } from "../../lib/backend";

const API = BACKEND_URL + "/api";

const KEY_LABELS = {
  STRIPE_SECRET_KEY: "Stripe secret key",
  STRIPE_WEBHOOK_SECRET: "Stripe webhook secret",
  STRIPE_PRICE_PRO_MONTHLY: "PRO monthly price ID",
  STRIPE_PRICE_PRO_YEARLY: "PRO yearly price ID",
  REVENUECAT_STRIPE_PUBLIC_API_KEY: "RevenueCat Stripe key",
};

const card = "rounded-2xl p-6";
const cardShadow = { boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 4px 24px rgba(25,28,29,0.04)" };

export default function BillingTab({ dm, authHeader }) {
  const [readiness, setReadiness] = useState(null);
  const [status, setStatus] = useState(null);
  const [selfTest, setSelfTest] = useState(null);
  const [runningTest, setRunningTest] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/billing/readiness`, { headers: authHeader() }),
        axios.get(`${API}/admin/billing/live-status`, { headers: authHeader() }),
      ]);
      setReadiness(r1.data);
      setStatus(r2.data);
    } catch {
      toast.error("Failed to load billing status");
    }
  }, [authHeader]);

  useEffect(() => { load(); }, [load]);

  const applyToggle = async (enabled) => {
    setToggling(true); setConfirming(false);
    try {
      await axios.put(`${API}/admin/billing/toggle`, { enabled }, { headers: authHeader() });
      toast.success(enabled ? "Payments ENABLED — live within 60s" : "Payments disabled");
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail?.message || "Toggle failed");
    } finally {
      setToggling(false);
    }
  };

  const onToggle = (next) => {
    if (next) setConfirming(true);   // enabling → explicit confirm
    else applyToggle(false);
  };

  const runSelfTest = async () => {
    setRunningTest(true); setSelfTest(null);
    try {
      const r = await axios.post(`${API}/admin/billing/self-test`, {}, { headers: authHeader() });
      setSelfTest(r.data);
      r.data.passed ? toast.success("Self-test passed") : toast.error("Self-test found problems");
    } catch {
      toast.error("Self-test failed to run");
    } finally {
      setRunningTest(false);
    }
  };

  const heading = dm ? "text-slate-100" : "text-[#191c1d]";
  const muted = dm ? "text-slate-500" : "text-[#6b7a7d]";
  const bg = dm ? "bg-[#1a2122]" : "bg-white";
  const ready = readiness?.ready;
  const enabled = readiness?.enabled;

  return (
    <TabsContent value="billing" data-testid="billing-tab">
      <div className="max-w-3xl">
        <div className="mb-6">
          <h2 className={`editorial-heading text-2xl font-medium ${heading}`}>Billing Control</h2>
          <p className={`category-tag mt-1 ${muted}`}>Enable and manage web payments without touching .env or redeploying. Secrets live in the encrypted store (Keys tab → Billing secrets).</p>
        </div>

        {/* Master toggle */}
        <div className={`${card} ${bg} mb-5`} style={cardShadow}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-semibold flex items-center gap-2 ${heading}`}>
                Payments enabled
                <span data-testid="billing-enabled-badge"
                  className={`text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-full ${enabled ? "bg-emerald-100 text-emerald-700" : dm ? "bg-white/10 text-slate-400" : "bg-black/5 text-[#6b7a7d]"}`}>
                  {enabled ? "LIVE" : "OFF"}
                </span>
              </p>
              <p className={`category-tag mt-0.5 ${muted}`}>
                {ready ? "Governs real web checkout availability. Applies within 60s, no redeploy."
                  : "Disabled — complete the readiness checklist below first."}
              </p>
            </div>
            <Switch
              checked={!!enabled}
              disabled={toggling || (!enabled && !ready)}
              onCheckedChange={onToggle}
              className="data-[state=checked]:bg-[#006874]"
              data-testid="billing-master-toggle"
            />
          </div>
          {confirming && (
            <div data-testid="billing-toggle-confirm" className={`mt-4 rounded-xl p-4 flex items-center justify-between gap-3 ${dm ? "bg-amber-400/10" : "bg-amber-50"}`}>
              <p className={`text-xs ${dm ? "text-amber-200" : "text-amber-800"}`}>
                <ShieldAlert className="inline w-3.5 h-3.5 mr-1" />
                Enable live payments? Real customers will be able to subscribe via Stripe Checkout
                {readiness?.stripe_key_mode === "live" ? " with the LIVE key." : "."}
              </p>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => applyToggle(true)} data-testid="billing-toggle-confirm-btn"
                  className="category-tag px-4 py-2 rounded-full bg-[#006874] text-white">Enable payments</button>
                <button onClick={() => setConfirming(false)} data-testid="billing-toggle-cancel-btn"
                  className={`category-tag px-4 py-2 rounded-full ${dm ? "bg-white/10 text-slate-300" : "bg-black/5 text-[#3b494c]"}`}>Cancel</button>
              </div>
            </div>
          )}
          {!ready && readiness && (
            <p data-testid="billing-missing-hint" className={`category-tag mt-3 ${dm ? "text-amber-300" : "text-amber-700"}`}>
              Missing: {readiness.missing.map(m => KEY_LABELS[m] || m).join(", ")}
            </p>
          )}
        </div>

        {/* Readiness checklist */}
        <div className={`${card} ${bg} mb-5`} style={cardShadow}>
          <div className="flex items-center justify-between mb-4">
            <p className={`category-tag ${muted}`}>READINESS</p>
            <div className="flex items-center gap-2">
              {readiness?.stripe_key_mode && readiness.stripe_key_mode !== "unknown" && (
                <span data-testid="billing-key-mode"
                  className={`text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-full ${readiness.stripe_key_mode === "live" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  STRIPE {readiness.stripe_key_mode.toUpperCase()} MODE
                </span>
              )}
              <button onClick={load} className={`p-1 rounded-full ${muted}`} data-testid="billing-refresh-btn" title="Refresh">
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {(readiness?.keys || []).map(k => (
              <div key={k.name} data-testid={`billing-ready-${k.name}`} className="flex items-center justify-between py-1.5">
                <div className="flex items-center gap-2.5">
                  {k.configured
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                    : <XCircle className={`w-4 h-4 shrink-0 ${k.optional ? (dm ? "text-slate-600" : "text-[#bac9cc]") : "text-red-500"}`} />}
                  <span className={`text-sm ${heading}`}>{KEY_LABELS[k.name] || k.name}</span>
                </div>
                <span className={`category-tag ${muted}`}>
                  {k.optional ? (k.configured ? "configured" : "optional — fallback local if missing") : k.configured ? "configured" : "missing"}
                </span>
              </div>
            ))}
            <div className={`flex items-center justify-between py-1.5 border-t mt-2 pt-3 ${dm ? "border-white/5" : "border-black/5"}`}>
              <span className={`text-sm ${heading}`}>Stripe webhook last event</span>
              <span data-testid="billing-webhook-last" className={`category-tag ${muted}`}>
                {readiness?.webhook_last_event_at ? new Date(readiness.webhook_last_event_at).toLocaleString() : "never received"}
              </span>
            </div>
          </div>
        </div>

        {/* Live status */}
        <div className={`${card} ${bg} mb-5`} style={cardShadow}>
          <p className={`category-tag mb-4 ${muted}`}>LIVE STATUS</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {[
              { icon: Users, label: "PRO users", value: status?.pro_total, testid: "billing-status-pro-total" },
              { icon: TrendingUp, label: "New PRO 30d", value: status?.new_pro_30d, testid: "billing-status-new-30d" },
              { icon: Activity, label: "Active Stripe subs", value: status?.active_stripe_subs, testid: "billing-status-subs" },
              { icon: CreditCard, label: "MRR", value: status && "mrr" in status ? (status.mrr ?? "n/a") : "—", testid: "billing-status-mrr" },
            ].map(({ icon: Icon, label, value, testid }) => (
              <div key={label} data-testid={testid} className={`rounded-xl p-3 ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
                <Icon className={`w-3.5 h-3.5 mb-1.5 ${dm ? "text-cyan-400" : "text-[#006874]"}`} />
                <p className={`text-xl font-medium editorial-heading ${heading}`}>{value ?? "…"}</p>
                <p className={`category-tag ${muted}`}>{label}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mb-4" data-testid="billing-by-source">
            {status && Object.entries(status.by_source || {}).map(([src, n]) => (
              <span key={src} className={`category-tag px-2.5 py-1 rounded-full ${dm ? "bg-white/5 text-slate-300" : "bg-black/5 text-[#3b494c]"}`}>
                {src}: {n}
              </span>
            ))}
          </div>
          <p className={`category-tag mb-2 ${muted}`}>RECENT PRO TRANSITIONS</p>
          {status?.recent_transitions?.length ? (
            <div className="space-y-1" data-testid="billing-transitions">
              {status.recent_transitions.map((t, i) => (
                <div key={i} className={`flex items-center justify-between text-xs py-1 ${dm ? "text-slate-400" : "text-[#3b494c]"}`}>
                  <span className="truncate mr-2">{t.email}</span>
                  <span className="shrink-0">
                    <span className={t.to ? "text-emerald-500" : "text-red-500"}>{t.from ? "PRO" : "free"} → {t.to ? "PRO" : "free"}</span>
                    <span className={`ml-2 ${muted}`}>{t.source}{t.at ? ` · ${new Date(t.at).toLocaleDateString()}` : ""}</span>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className={`text-xs ${muted}`} data-testid="billing-transitions-empty">No PRO transitions recorded yet.</p>
          )}
        </div>

        {/* Actions */}
        <div className={`${card} ${bg}`} style={cardShadow}>
          <p className={`category-tag mb-4 ${muted}`}>ACTIONS</p>
          <button onClick={runSelfTest} disabled={runningTest} data-testid="billing-selftest-btn"
            className={`w-full py-3 rounded-full category-tag transition-all disabled:opacity-50 flex items-center justify-center gap-2 ${dm ? "bg-cyan-400 text-[#001f24] hover:bg-cyan-300" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}>
            {runningTest ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Running self-test…</> : <><FlaskConical className="w-3.5 h-3.5" />Run billing self-test</>}
          </button>
          <p className={`category-tag mt-2 ${muted}`}>Creates + immediately expires a Checkout Session on a throwaway customer (then deleted). Zero charges, zero leftovers. Works while payments are OFF.</p>

          {selfTest && (
            <div data-testid="billing-selftest-result" className={`mt-4 rounded-xl p-4 ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-sm font-semibold ${selfTest.passed ? "text-emerald-500" : "text-red-500"}`}>
                  {selfTest.passed ? "All checks passed" : "Checks failed"}
                </span>
                <span className={`text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-full ${selfTest.stripe_key_mode === "live" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  KEY: {String(selfTest.stripe_key_mode || "unknown").toUpperCase()}
                </span>
              </div>
              {(selfTest.checks || []).map(c => (
                <div key={c.name} data-testid={`billing-check-${c.name}`} className="flex items-center gap-2 py-0.5">
                  {c.passed ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                  <span className={`text-xs ${heading}`}>{c.name.replaceAll("_", " ")}</span>
                  {c.detail && <span className={`text-xs truncate ${muted}`}>— {c.detail}</span>}
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-4">
            {[
              { label: "Stripe Dashboard — Billing", href: "https://dashboard.stripe.com/billing", testid: "billing-link-stripe" },
              { label: "Stripe — Webhooks", href: "https://dashboard.stripe.com/webhooks", testid: "billing-link-webhooks" },
              { label: "RevenueCat project", href: "https://app.revenuecat.com/", testid: "billing-link-revenuecat" },
            ].map(l => (
              <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer" data-testid={l.testid}
                className={`category-tag flex items-center gap-1.5 px-3 py-1.5 rounded-full ${dm ? "bg-white/5 text-slate-300 hover:bg-white/10" : "bg-black/5 text-[#3b494c] hover:bg-black/10"}`}>
                {l.label}<ExternalLink className="w-3 h-3" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </TabsContent>
  );
}
